import type { LucideIcon } from 'lucide-react';
import { Sparkles, Rocket, Building2 } from 'lucide-react';

export type TierId = 'free' | 'pro' | 'business';
export type BillingCycle = 'monthly' | 'annual';

export interface TierFeature {
  label: string;
  free: string | boolean;
  pro: string | boolean;
  business: string | boolean;
}

export interface Tier {
  id: TierId;
  name: string;
  icon: LucideIcon;
  tagline: string;
  priceMonthly: number;
  priceAnnual: number;
  annualSavings: number;
  highlight: boolean;
  badge?: string;
  ctaLabel: string;
  ctaIntent: 'register' | 'upgrade';
  summaryFeatures: string[];
}

// Source of truth: EPIC.md epic-plans-billing + BP-LIMITS-UPDATE 2026-04-17
// Free: 5 forms / 50 resp/mês / 20 fields
// Pro: 30 forms / 800 resp/mês / fields ilimitado
// Business: ilimitado em todas as dimensões

export const TIERS: readonly Tier[] = [
  {
    id: 'free',
    name: 'Free',
    icon: Sparkles,
    tagline: 'Para começar a testar ideias',
    priceMonthly: 0,
    priceAnnual: 0,
    annualSavings: 0,
    highlight: false,
    ctaLabel: 'Começar grátis',
    ctaIntent: 'register',
    summaryFeatures: [
      '5 formulários ativos',
      '50 respostas/mês',
      '20 campos por formulário',
      'Todos os 25 tipos de campo',
      'Temas personalizados',
      'Exportação CSV',
      'Watermark OneForm (obrigatório)',
      'Suporte comunidade',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    icon: Rocket,
    tagline: 'Para quem coleta sério',
    priceMonthly: 47,
    priceAnnual: 451,
    annualSavings: 113,
    highlight: true,
    badge: 'Mais popular',
    ctaLabel: 'Assinar Pro',
    ctaIntent: 'upgrade',
    summaryFeatures: [
      '30 formulários ativos',
      '800 respostas/mês',
      'Campos ilimitados por formulário',
      'Watermark removível',
      'Logo personalizado',
      'Notificações por email',
      '3 webhooks',
      'Respostas parciais',
      'PDF individual',
      'Redirect URL',
      'Fluxos condicionais completos',
      'Suporte email 48h',
    ],
  },
  {
    id: 'business',
    name: 'Business',
    icon: Building2,
    tagline: 'Para escala sem limites',
    priceMonthly: 97,
    priceAnnual: 931,
    annualSavings: 233,
    highlight: false,
    ctaLabel: 'Assinar Business',
    ctaIntent: 'upgrade',
    summaryFeatures: [
      'Formulários ilimitados',
      'Respostas ilimitadas',
      'Campos ilimitados',
      'Sem watermark',
      'Webhooks ilimitados',
      'PDF individual',
      'PDF relatório agregado',
      '5 assentos de equipe',
      'Fluxos condicionais completos',
      'Redirect URL',
      'Suporte prioritário 24h',
    ],
  },
] as const;

// Tabela comparativa completa (todas as 17 dimensões do EPIC.md)
export const COMPARISON_FEATURES: readonly TierFeature[] = [
  { label: 'Formulários ativos', free: '5', pro: '30', business: 'Ilimitado' },
  { label: 'Respostas por mês', free: '50', pro: '800', business: 'Ilimitado' },
  { label: 'Campos por formulário', free: '20', pro: 'Ilimitado', business: 'Ilimitado' },
  { label: 'Tipos de campo (25)', free: true, pro: true, business: true },
  { label: 'Temas personalizados', free: true, pro: true, business: true },
  { label: 'Watermark OneForm', free: 'Obrigatório', pro: 'Removível', business: 'Sem watermark' },
  { label: 'Logo personalizado', free: false, pro: true, business: true },
  { label: 'Notificações por email', free: false, pro: true, business: true },
  { label: 'Webhooks', free: false, pro: '3', business: 'Ilimitado' },
  { label: 'Respostas parciais', free: false, pro: true, business: true },
  { label: 'Exportação CSV', free: true, pro: true, business: true },
  { label: 'PDF individual', free: false, pro: true, business: true },
  { label: 'PDF relatório agregado', free: false, pro: false, business: true },
  { label: 'Fluxos condicionais', free: 'Básico (1 condição)', pro: 'Completo', business: 'Completo' },
  { label: 'Redirect URL', free: false, pro: true, business: true },
  { label: 'Assentos de equipe', free: '1', pro: '1', business: '5' },
  { label: 'Suporte', free: 'Comunidade', pro: 'Email 48h', business: 'Prioritário 24h' },
] as const;

export interface FaqItem {
  question: string;
  answer: string;
}

// OQ4 Pax: 7 perguntas aprovadas 2026-04-17
export const FAQ_ITEMS: readonly FaqItem[] = [
  {
    question: 'Posso cancelar a qualquer momento?',
    answer:
      'Sim. Você pode cancelar sua assinatura a qualquer momento diretamente no painel de cobrança. O acesso ao plano continua até o fim do período já pago — sem multas e sem prorrogação automática após o cancelamento.',
  },
  {
    question: 'Vocês aceitam PIX ou boleto?',
    answer:
      'Em breve. No momento aceitamos cartão de crédito via Stripe (Visa, Mastercard, Elo e Amex). Estamos preparando suporte a PIX e boleto como próximos métodos de pagamento.',
  },
  {
    question: 'O que acontece se eu exceder o limite de respostas?',
    answer:
      'No plano Free, novas respostas são pausadas até o próximo ciclo mensal ou até você fazer upgrade para Pro/Business. Nenhuma resposta é perdida — o usuário final vê uma mensagem amigável e você recebe um alerta por email.',
  },
  {
    question: 'Tem desconto no plano anual?',
    answer:
      'Sim, 20% de desconto em ambos os planos pagos. Pro: R$ 47/mês ou R$ 451/ano (economia de R$ 113). Business: R$ 97/mês ou R$ 931/ano (economia de R$ 233). A cobrança é anual única no início do ciclo.',
  },
  {
    question: 'Posso trocar de plano depois?',
    answer:
      'Sim, a qualquer momento. Upgrades são imediatos (acesso liberado na hora, cobrança proporcional). Downgrades aplicam no próximo ciclo — você mantém o plano atual até o fim do período pago.',
  },
  {
    question: 'Preciso de cartão para começar no Free?',
    answer:
      'Não. O plano Free é realmente grátis e não requer cartão no cadastro. Cartão só é solicitado ao escolher Pro ou Business.',
  },
  {
    question: 'Tenho direito a reembolso?',
    answer:
      'Oferecemos reembolso integral em até 7 dias após a primeira cobrança de qualquer plano pago, conforme o Código de Defesa do Consumidor. Basta entrar em contato pelo email de suporte.',
  },
] as const;
